<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="registration-form__username-password-container">
    <h3 class="registration-form__subtitle">Create your username and password:</h3>
    <div v-if="isAuthErrorNotificationVisible" 
         class="registration-form__two-factor-notification">
      <UtdNotification id="twoFactorNotification"
                       :has-close-notification="true"
                       notification-mode="error"
                       @closed="handleCloseAuthErrorNotification">
        <template #title>
          <h4>Submission Error</h4>
        </template>
        <template #listContent>
          <ul class="registration-form__auth-notification-list">
            <li v-for="(message, index) in authErrorMessages" 
                :key="index"
                class="registration-form__auth-notification-list-item">
              <span v-html="message" />
            </li>
          </ul>
        </template>
      </UtdNotification>
    </div>
    <fieldset v-show="formFields.registrationUserName 
                && !formFields.registrationUserName.isHidden" 
              id="userNameField"
              class="wk-js2 registration-form-field">
      <UtdTextInput label="Username"
                    :is-error="checkFieldInvalid(REG_FIELD.USER_NAME) 
                      && isDirty.registrationUserName"
                    :error-message="$t(userNameErrorMessage())"
                    :required="formFields.registrationUserName?.required">
        <input id="registrationUserName"
               v-model="form.registrationUserName"
               aria-label="Username"
               :maxlength="formFields.registrationUserName?.maxLength"
               type="text"
               class="wk-field-input form-field jumpstart-input"
               autocomplete="username"
               @input="checkResetServerError(REG_FIELD.USER_NAME)"
               @blur="() => isDirty.registrationUserName = true">
      </UtdTextInput>
    </fieldset>
    
    <fieldset id="passwordField"
              class="wk-js2 registration-form-field">
      <UtdPasswordInput :label="$t('LOGIN.PASSWORD')"
                        :is-error="checkFieldInvalid(REG_FIELD.PASSWORD) && isDirty.password"
                        :error-message="$t(passwordErrorMessage())"
                        :required="formFields.password?.required"
                        @text-input-toggle-show="togglePasswordVisibleInput">
        <input id="password"
               v-model="form.password"
               :type="newPasswordInputType"
               :maxlength="formFields.password?.maxLength"
               aria-label="Password"
               class="wk-field-input form-field jumpstart-input"
               autocomplete="new-password"
               @input="checkResetServerError(REG_FIELD.PASSWORD)"
               @blur="() => isDirty.password = true">
      </UtdPasswordInput>
    </fieldset>

    <fieldset class="registration-form__passwword-rules">
      <h4>Password rules:</h4>
      <PasswordRules class="ds1-ml-1" />
    </fieldset>

    
    <fieldset id="verifyPasswordField"
              class="wk-js2 registration-form-field">
      <UtdPasswordInput label="Verify Password"
                        :is-error="checkFieldInvalid(REG_FIELD.VERIFY_PASSWORD) 
                          && isDirty.verifyPassword"
                        :error-message="$t(getFieldErrorMessage(REG_FIELD.VERIFY_PASSWORD))"
                        :required="formFields.verifyPassword?.required"
                        @text-input-toggle-show="toggleVerifyPasswordVisibleInput">
        <input id="verifyPassword"
               v-model="form.verifyPassword"
               :type="verifyPasswordInputType"
               :maxlength="formFields.verifyPassword?.maxLength"
               aria-label="Verify Password"
               class="wk-field-input form-field jumpstart-input"
               autocomplete="new-password"
               @input="checkResetServerError(REG_FIELD.VERIFY_PASSWORD)"
               @blur="() => isDirty.verifyPassword = true">
      </UtdPasswordInput>
    </fieldset>

    <fieldset v-if="hasSentVerificationCode" 
              class="registration-form__two-factor-action 
                          wk-js2 registration-form-field">
      <div v-if="hasResentVerificationCode" 
           class="registration-form__resend-code-text">
        <i class="wkce-icon-filled-check-circle" />
        <p>A new code has been sent</p>
      </div>
      <UtdButton id="resendVerificationButton"
                 button-color="green-shade-3"
                 button-size="large"
                 type="button"
                 :disabled="isSendNewCodeDisabled || !isAllowOtcGeneration || isSubmitting"
                 @click.prevent="emit('sendVerificationCode', { resendCode: true })">
        Send New Code
      </UtdButton>
    </fieldset>

    <div v-if="hasSentVerificationCode && isAuthNotificationVisible" 
         class="registration-form__two-factor-notification">
      <UtdNotification id="twoFactorNotification"
                       type="info"
                       notification-mode="info">
        <template #title>
          <h4>A code was sent to you via email</h4>
        </template>
        <template #listContent>
          <ul class="registration-form__auth-notification-list">
            <li v-for="(message, index) in UTD_TWO_FACTOR_AUTH_MESSAGES" 
                :key="index"
                class="registration-form__auth-notification-list-item">
              <span v-html="message" />
            </li>
          </ul>
        </template>
      </UtdNotification>
    </div>

    <fieldset v-show="hasSentVerificationCode" 
              id="verificationCodeField"
              class="wk-js2 registration-form-field">
      <UtdTextInput label="Verification Code"
                    :is-error="invalidVerificationCode()
                      && isDirty.verificationCode"
                    :error-message="$t(verificationCodeErrorMessage())"
                    :required="true">
        <input id="verificationCodeInput"
               v-model="form.verificationCode"
               aria-label="Verification Code"
               :maxlength="MAX_VERIFICATION_CODE_LENGTH"
               type="text"
               class="wk-field-input form-field jumpstart-input"
               autocomplete="off"
               placeholder="Enter or copy the code from your email"
               @keyup.enter="handleVerificationCodeSubmit"
               @input="checkResetServerError(REG_FIELD.VERIFICATION_CODE)"
               @blur="() => isDirty.verificationCode = true">
      </UtdTextInput>
    </fieldset>
  </div>
</template>

<script setup>
import UtdTextInput from '_acaSrc/components/shared/input/UtdTextInput.vue';
import UtdPasswordInput from '_acaSrc/components/shared/input/UtdPasswordInput.vue';
import UtdButton from '_acaSrc/components/shared/stdlib/UtdButton.vue';
import PasswordRules from '_acaSrc/components/login/PasswordRules.vue';
import useRegistrationForm from '_acaSrc/composables/registrationForm.composable';
import { useStore } from 'vuex';
import { ref, computed } from 'vue';
import UtdNotification from '_acaSrc/components/shared/utd/UtdNotification.vue';
import { 
    UTD_TWO_FACTOR_AUTH_MESSAGES, 
    UTD_TWO_FACTOR_AUTH_ERROR_MESSAGES, 
    REG_FIELD
} from '_acaSrc/utility/constants';

const MAX_VERIFICATION_CODE_LENGTH = 24;

const store = useStore();

const registrationForm = computed(() => store.getters['login/registrationForm']);
const formFields = computed(() => registrationForm.value.formFields);

const emit = defineEmits([
    'sendVerificationCode',
    'closeAuthErrorNotification',
    'submitForm'
]);
const {
    hasSentVerificationCode,
    hasResentVerificationCode,
    isAuthNotificationVisible,
    isAuthErrorNotificationVisible,
    isSubmitting
} = defineProps({
    hasSentVerificationCode: {
        type: Boolean,
        default: false
    },
    hasResentVerificationCode: {
        type: Boolean,
        default: false
    },
    isAuthNotificationVisible: {
        type: Boolean,
        default: false
    },
    isAuthErrorNotificationVisible: {
        type: Boolean,
        default: false
    },
    isAllowOtcGeneration: {
        type: Boolean,
        default: true
    },
    isSubmitting: {
        type: Boolean,
        default: false
    }
});

const handleCloseAuthErrorNotification = () => {
    emit('closeAuthErrorNotification');
};

const newPasswordInputType = ref('password');
const verifyPasswordInputType = ref('password');

const togglePasswordVisibleInput = (hideText) => {
    newPasswordInputType.value = hideText ? 'password' : 'text';
};

const toggleVerifyPasswordVisibleInput = (hideText) => {
    verifyPasswordInputType.value = hideText ? 'password' : 'text';
};

const authErrorMessages = computed(() => {
    const errorMessages = [];
    if (serverError.twoFactor) {
        errorMessages.push(...serverError.twoFactor);
    }
    else {
        errorMessages.push(...UTD_TWO_FACTOR_AUTH_ERROR_MESSAGES);
    }
    return errorMessages;
});

const handleVerificationCodeSubmit = () => {
    if (isDirty.verificationCode && !checkFieldInvalid(REG_FIELD.VERIFICATION_CODE)) {
        emit('submitForm');
    }
};

const {
    form,
    serverError,
    isDirty,
    isSendNewCodeDisabled,
    checkResetServerError,
    checkFieldInvalid,
    getFieldErrorMessage,
    invalidVerificationCode,
    verificationCodeErrorMessage,
    userNameErrorMessage,
    passwordErrorMessage
} = useRegistrationForm();
</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';
@DS1-UTD-ERROR-ON-BLUE-BG-COLOR: @WKCE-RED-SHADE1;

@media only screen and (min-width: 768px) {
  .registration-form__username-password-container {
    .ds1-mh-2();
  }
}

.registration-form__passwword-rules {
  .ds1-mb-2();
  border: none;
}

.registration-form__two-factor-action {
  .ds1-flex-vertical-center();
  justify-content: flex-end;
  flex-wrap: wrap-reverse;
  gap: 8px;
}

.registration-form__resend-code-text {
  .ds1-ma-1();
  .ds1-flex-vertical-center();
  .ds1-utd-weight-600();
  gap: 4px;
  font-size: 16px;
}

.wkce-icon-filled-check-circle {
  color: @DS1-UTD-SUCCESS;
  font-size: 20px;
}

.registration-form__auth-notification-list {
  .ds1-mv-1();
  .ds1-pl-3();
  width: 85%;
  color: @DS1-UTD-TOPIC-TEXT-COLOR;
}

.registration-form__auth-notification-list-item {
  margin-bottom: 4px;
}

.registration-form__two-factor-notification {
  .ds1-ph-1();
  .ds1-mb-2();
}

.registration-form__username-password-container {
  .ds1-mt-3();
}

</style>